import { API_URLS, ASYNC_STATE, MAPPED_IDS } from '@brighterion/utils/constants';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useAppStore } from '../../store/index';
import { getApi } from '../../services/apiService';
import { RoleService } from '../../services';

const GlobalPollerManager = () => {
    const startPolling = useAppStore.use.startPolling?.();
    const showAlert = useAppStore.use.showAlert?.();
    const setConfigStatus = useAppStore.use.setConfigStatus?.();
    const setOperationBy = useAppStore.use.setOperationBy?.();
    const queryClient = useQueryClient();
    const PENDING_HISTORY_QUERY_KEY = ['PendingHistoryData'];
    const GET_CONFIG_QUERY_KEY = ['ConfigurationData'];
    const successMessage = ASYNC_STATE.CONFIG_CHANGES_SUCCESS_MESSAGE;
    const failMessage = ASYNC_STATE.CONFIG_CHANGES_FAIL_MESSAGE;
    const setListPollingData = useAppStore.use.setListPollingData?.();
    const { hasWLSConfigAdminAccess, hasWLSListAdminAccess } = RoleService;
    const prevStatusMap = new Map();
    const tempConfigurationStatus = '';

    const startPollingConfig = () => {
        if (!hasWLSConfigAdminAccess) return;

        startPolling({
            id: 'getConfigStatusPolling',
            apiCall: () => getApi({ url: API_URLS.getConfigStatusPolling }),
            interval: ASYNC_STATE.POLLING_INTERVAL,
            onSuccess: (responseData) => {
                const { status, operationBy } = responseData.data || {};
                const prevStatus = tempConfigurationStatus;

                if (
                    (status === ASYNC_STATE.APPROVED_STATUS || status === ASYNC_STATE.FAILED_STATUS) &&
                    (prevStatus === ASYNC_STATE.PROCESSING_STATUS ||
                        prevStatus === ASYNC_STATE.CONFIG_NEEDS_REVIEW_STATUS)
                ) {
                    queryClient?.invalidateQueries({ queryKey: PENDING_HISTORY_QUERY_KEY });
                    queryClient?.invalidateQueries({ queryKey: GET_CONFIG_QUERY_KEY });

                    const alertMessage = status === ASYNC_STATE.APPROVED_STATUS ? successMessage : failMessage;
                    const alertType = status === ASYNC_STATE.APPROVED_STATUS ? 'success' : 'danger';
                    const alertLinkText = status === ASYNC_STATE.FAILED_STATUS ? 'View configuration' : '';
                    const alertLinkTo = status === ASYNC_STATE.FAILED_STATUS ? '/configuration' : '';

                    showAlert({
                        type: alertType,
                        message: alertMessage,
                        linkText: alertLinkText,
                        linkTo: alertLinkTo,
                        linkType: 'link'
                    });
                }

                setConfigStatus(status);
                setOperationBy({ ...operationBy });
            },
            onError: (error) => console.error('Configuration status Polling error:', error)
        });
    };

    const startPollingList = () => {
        if (!hasWLSListAdminAccess) return;

        startPolling({
            id: 'getInternListStatusPolling',
            apiCall: () => getApi({ url: API_URLS.getInternListStatusPolling }),
            interval: ASYNC_STATE.POLLING_INTERVAL,
            onSuccess: (responseData) => {
                const newData = responseData?.data?.changes || [];
                newData.forEach((item: any) => {
                    const prevStatus = prevStatusMap.get(item.listId);
                    if (prevStatus && prevStatus !== item.status) {
                        if (item.status === 'approved') {
                            showAlert({
                                type: 'success',
                                message: ASYNC_STATE.LIST_CHANGES_SUCCESS_MESSAGE(item.listName, item.revisionId)
                            });
                        } else if (item.status === 'failed') {
                            const id = MAPPED_IDS.get(item?.listName);
                            showAlert({
                                type: 'danger',
                                message: ASYNC_STATE.LIST_CHANGES_FAIL_MESSAGE(item.listName, item.revisionId),
                                linkText: 'View list revision',
                                linkTo: `/lists/${id}/revision-history`,
                                linkType: 'link'
                            });
                        }
                    }
                    prevStatusMap.set(item.listId, item.status);
                });
                setListPollingData(newData);
            },
            onError: (error) => console.error('Polling error in internal list polling:', error)
        });
    };

    useEffect(() => {
        // Start polling for configuration status
        startPollingConfig();

        // Start polling for list status
        startPollingList();
    }, [hasWLSConfigAdminAccess, hasWLSListAdminAccess, successMessage, failMessage]);

    return null;
};

export default GlobalPollerManager;
